import React, { useEffect } from "react";
import { Avatar, Button, Grid, Space, Typography, Tag, Tooltip } from "antd";
import { t } from "i18next";
import {
    UserOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    MedicineBoxOutlined,
    BankOutlined,
    TagsOutlined,
    IdcardOutlined,
    EyeOutlined
} from "@ant-design/icons";
import moment from "moment";

// Servisler ve Contextler
import ApiService from "../../../core/service/ApiService";
import DataGrid from "../../../core/components/data-grid/DataGrid";
import { useDataGrid } from "../../../core/components/data-grid/DataGridContext";
import { useModal } from "../../../core/context/ModalContext";
import Helper from "../../../core/Helper";
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";

// Custom Bileşenler
import { AppointmentSummaryRow } from "./AppointmentSummaryRow";
import { AppointmentSummaryCard } from "./AppointmentSummaryCard";
import AppointmentStateColumn from "./AppointmentStateColumn";
import AppointmentIdColumn, { AppointmentLink } from "./AppointmentIdColumn";
import AppointmentPreviewApp from "../preview/AppointmentPreviewApp";
import AppointmentCard from "../AppointmentCard";
import MobileActionsMenu from "./MobileActionsMenu";
import AppointmentExcelButton from "./AppointmentExcelButton";

const { Text } = Typography;

export default function AppointmentDataGrid({ hideSummary = false, toolbarExtra, excelView }) {
    const { setData, setPaginationResult, queryModel, setDataGridProps, refreshDataGrid } = useDataGrid();
    const screens = Grid.useBreakpoint();
    const { formatPrice, hasPermission, user } = useUser();
    const { openModal } = useModal();

    const openAppointmentPreview = (row) => {
        openModal(
            t('Appointment Preview'),
            <AppointmentPreviewApp
                appointmentId={row.appointmentId}
                onDelete={() => refreshDataGrid()}
            />,
            () => refreshDataGrid()
        );
    };

    // Veri Yükleme
    const load = async () => {
        const input = {
            ...queryModel,
            filter: {
                ...queryModel.filter,
                startDate: Helper.Date.beginOfDay(queryModel.filter.startDate),
                endDate: Helper.Date.endOfDay(queryModel.filter.endDate),
            }
        }

        if (!Object.keys(input.filter).includes('isDeleted')) {
            input.filter.isDeleted = false
        }

        const res = await ApiService.Appointment.GetAppointmentDataGrid(input);
        if (res.success) {
            setData(res.data)
            setPaginationResult(res.pagination)
        }
    }

    // Modern Kolon Tasarımı
    const getColumns = () => {
        return [
            {
                width: 90,
                key: 'appointmentId',
                dataIndex: 'appointmentId',
                title: t('Id'),
                // fixed: 'left', // İSTEK 1: Sabitleme kaldırıldı
                render: (text, row) => <AppointmentIdColumn text={text} row={row} />
            },
            {
                key: 'patientName',
                dataIndex: 'patientName',
                title: t('Patient'),
                // width: 250,
                render: (text, row) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Avatar
                            size={24}
                            icon={<UserOutlined />}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <AppointmentLink row={row}>
                                <Text strong style={{ color: 'inherit' }}>{row?.patientName} {row?.patientLastname}</Text>
                            </AppointmentLink>
                            <Space size={4}>
                                <Text type="secondary" style={{ fontSize: 11 }}>{row?.patientCustomId}</Text>
                                {row?.otherPatientId && <Tag style={{ fontSize: 10, lineHeight: '16px' }}>Diğer: {row?.otherPatientId}</Tag>}
                            </Space>
                        </div>
                    </div>
                )
            },
            {
                key: 'startDate',
                dataIndex: 'startDate',
                sorter: true,
                title: t('Date'),
                width: 140,
                render: (text, row) => (
                    <AppointmentLink row={row}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <CalendarOutlined style={{ color: '#8c8c8c' }} />
                            <Text strong style={{ color: 'inherit' }}>{moment(text).format('DD.MM.YYYY')}</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {moment(text).format('HH:mm')} - {moment(row.endDate).format('HH:mm')}
                            </Text>
                        </div>
                    </div>
                    </AppointmentLink>
                )
            },
            {
                key: 'unitName',
                dataIndex: 'unitName',
                columnName: 'unit.name',
                sorter: true,
                title: t('Unit'),
                width: 150,
                render: (text) => (
                    <Space>
                        <MedicineBoxOutlined style={{ color: '#1890ff' }} />
                        <Text style={{ fontSize: 13 }}>{text}</Text>
                    </Space>
                )
            },
            {
                key: 'institutionName',
                dataIndex: 'institutionName',
                columnName: 'institution.name',
                sorter: true,
                title: t('Institution'),
                width: 150,
                render: (text) => (
                    <Space>
                        <BankOutlined style={{ color: '#fa8c16' }} />
                        <Tooltip title={text}>
                            <Text style={{ maxWidth: 130 }} ellipsis>{text}</Text>
                        </Tooltip>
                    </Space>
                )
            },
            // İSTEK 3: Randevu Türü Ayrı Sütuna Taşındı
            {
                key: 'appointmentTypeName',
                dataIndex: 'appointmentTypeName',
                columnName: 'appointmentType.name',
                sorter: true,
                title: t('Type'),
                width: 120,
                render: (text) => (
                    <Tooltip title={text}>
                        <Text style={{ maxWidth: 130 }} ellipsis>{text}</Text>
                    </Tooltip>
                )
            },
            {
                key: 'stateName',
                dataIndex: 'stateName',
                columnName: 'state.name',
                sorter: true,
                title: t('State'),
                width: 60,
                render: (text, row) => <AppointmentStateColumn text={text} row={row} />
            },
            // İSTEK 2: Finansal Kolonlar Ayrıldı (Sıralama için)
            {
                key: 'totalPrice',
                dataIndex: 'totalPrice',
                sorter: true,
                title: t('Price'),
                align: 'right',
                width: 110,
                render: (text, row) => (
                    <Text type="secondary">
                        {text === -1 ? '-' : formatPrice(text, { currency: row?.currency || 'TRY' })}
                    </Text>
                )
            },
            {
                key: 'totalPaid',
                dataIndex: 'totalPaid',
                sorter: true,
                title: t('Paid'),
                align: 'right',
                width: 110,
                render: (text, row) => (
                    <Text style={{ color: text > 0 ? '#389e0d' : 'inherit' }}>
                        {text === -1 ? '-' : formatPrice(text, { currency: row?.currency || 'TRY' })}
                    </Text>
                )
            },
            {
                key: 'balance',
                dataIndex: 'balance',
                title: t('Balance'),
                sorter: true,
                align: 'right',
                width: 120,
                render: (text, row) => {
                    const hasDebt = row.balance > 0;
                    if (row.totalPrice === -1) return '-';
                    return (
                        <Text strong style={{ color: hasDebt ? '#cf1322' : '#389e0d' }}>
                            {formatPrice(row.balance, { currency: row?.currency || 'TRY' })}
                        </Text>
                    )
                }
            },
        ]
    }

    useEffect(() => {
        load()
    }, [queryModel.updateKey])

    // --- MOBİL KART RENDER ---
    const renderCard = (row) => {
        const canViewOther = hasPermission(PERMISSIONS.APPOINTMENT_VIEW_OTHER_USER);
        const canPreview = canViewOther || row.responsibleUserId === user?.id;

        return (
            <AppointmentCard
                row={row}
                formatPrice={formatPrice}
                showPatient={true}
                showPreview={true}
                canPreview={canPreview}
                onPreview={openAppointmentPreview}
            />
        );
    };

    useEffect(() => {
        let columns = getColumns()

        let props = {
            rowKey: 'appointmentId',
            enableRowSelection: false,
            expandedRowRender: false,
            columns: columns,
            rowClassName: (row) => row.isDeleted ? 'deleted-row opacity-50' : 'modern-row',
            cardRender: renderCard,
            cardSummary: () => hideSummary ? null : <AppointmentSummaryCard />,
            virtual: true,
            virtualRowHeight: 47, // size: 'middle'
            antdTableProps: {
                summary: () => hideSummary ? null : <AppointmentSummaryRow />,
                scroll: { x: 1200 }, // Sütunlar ayrıldığı için scroll alanı biraz genişletildi
                size: 'middle'
            }
        }

        setDataGridProps(props)
    }, [])

    return (
        <DataGrid
            name={'appointments'}
            toolbarExtra={toolbarExtra}
            // Excel araç çubuğunun en sağında: mobilde İşlemler menüsünde, masaüstünde düğme
            title={excelView ? () => (screens.md === false
                ? <MobileActionsMenu excelView={excelView} />
                : <AppointmentExcelButton view={excelView} />) : undefined}
        />
    )
}